/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { Footer } from "@/components/landing/footer";
import { MarketingPageHero } from "@/components/landing/marketing-page-hero";
import { MarketingCTASection } from "@/components/landing/marketing-cta-section";
import { MarketingSectionHeader } from "@/components/landing/marketing-section-header";
import { StatCounter } from "@/components/landing/primitives";
import { EMAILS } from "@/lib/constants/master";
import MainNavigation from "@/components/layout/main-navigation";
import { Link2, Layers, Bot, Wrench } from "lucide-react";
import {
  AI_TOOL_COUNT,
  TRADES_COUNT,
  STARTING_PRICE,
} from "@/content/landing/constants";
import { FadeIn } from "@/components/landing/primitives/fade-in";

const whatWeBuilt = [
  {
    icon: Link2,
    title: "Connected, not complicated",
    description:
      "Estimates become proposals become projects become invoices. One flow, no re-entry, no switching apps.",
  },
  {
    icon: Layers,
    title: "Pre-loaded for your trade",
    description:
      "Pick your trade and the cost database, categories, and labor rates are already there. Adjust, don't build from scratch.",
  },
  {
    icon: Bot,
    title: "AI that knows your data",
    description: `${AI_TOOL_COUNT} tools. Ask Hank about your costs, your estimates, your margins — in plain English. AI suggests, you decide.`,
  },
  {
    icon: Wrench,
    title: "Built for specialty contractors",
    description:
      "Landscaping, HVAC, electrical, plumbing, roofing, concrete, GC, residential. Not enterprise software crammed into a smaller box.",
  },
];

const beliefs = [
  {
    title: "Simple beats powerful",
    description: "The best software is the one people actually use.",
  },
  {
    title: "Connected beats comprehensive",
    description: "Things that work together beat 50 standalone features.",
  },
  {
    title: "Your data, your decisions",
    description: "AI suggests. You decide. Always.",
  },
  {
    title: "Honest about where we are",
    description:
      "We're in early access. No fake testimonials. Real product, real cost data, real results.",
  },
];

const stats = [
  { value: TRADES_COUNT, label: "trades pre-loaded" },
  { value: AI_TOOL_COUNT, label: "AI assistant tools" },
  { value: STARTING_PRICE, label: "starting price per month", prefix: "$" },
];

export function AboutPageClient() {
  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />

      {/* Hero */}
      <MarketingPageHero
        variant="dark"
        badge="About Contractor Co-Pilot"
        title={
          <>
            Built by someone who grew up watching contractors{" "}
            <span className="bg-gradient-to-br from-secondary to-secondary/80 bg-clip-text text-transparent">
              lose money on bad systems
            </span>
          </>
        }
        description="Not because of bad work. Because of disconnected tools, pricing from memory, and spreadsheets that don't talk to anything."
      />

      {/* The Problem */}
      <section
        className="py-16 md:py-24 bg-background"
        aria-labelledby="the-problem"
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-16">
          <MarketingSectionHeader
            badge="The Problem"
            title="The problem nobody solves"
          />
          <FadeIn className="max-w-3xl mx-auto">
            <p className="text-lg text-muted-foreground leading-relaxed">
              Contractors run their businesses across 5 different tools —
              spreadsheets for estimates, email for proposals, QuickBooks for
              invoices, a notebook for schedules, and memory for costs. Nothing
              connects. Data gets re-entered. Numbers get lost. The tools that
              exist are either built for enterprise GCs running $50M projects,
              or they&apos;re glorified spreadsheets with a monthly fee.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* What We Built */}
      <section
        className="py-16 md:py-24 bg-background-warm"
        aria-labelledby="what-we-built"
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-16">
          <MarketingSectionHeader
            badge="What We Built"
            title="One place for your operations"
            description="Contractor Co-Pilot connects the things that should've always been connected."
          />

          <div className="grid md:grid-cols-2 gap-6">
            {whatWeBuilt.map((pillar, index) => {
              const Icon = pillar.icon;
              return (
                <FadeIn
                  key={pillar.title}
                  as="article"
                  className="card-premium bg-background p-6 rounded-2xl border border-border"
                  delay={index * 100}
                >
                  <div
                    className="mb-4 inline-flex items-center justify-center w-12 h-12 rounded-xl bg-secondary"
                    aria-hidden="true"
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-base font-bold mb-3 text-foreground font-display">
                    {pillar.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {pillar.description}
                  </p>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* By the Numbers */}
      <section
        className="py-16 md:py-24 bg-background"
        aria-labelledby="by-the-numbers"
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-16">
          <MarketingSectionHeader badge="By the Numbers" title="The details" />

          <div className="grid md:grid-cols-3 gap-8 max-w-3xl mx-auto">
            {stats.map((stat, index) => (
              <FadeIn
                key={stat.label}
                className="text-center bg-muted/30 rounded-2xl border border-border p-6"
                delay={index * 100}
              >
                <div className="text-[clamp(36px,5vw,56px)] font-bold font-display text-foreground">
                  {stat.prefix ?? ""}
                  <StatCounter value={stat.value} />
                </div>
                <p className="text-muted-foreground mt-2">{stat.label}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* What We Believe */}
      <section
        className="py-16 md:py-24 bg-background-warm-alt"
        aria-labelledby="our-values"
      >
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 lg:px-16">
          <MarketingSectionHeader
            badge="What We Believe"
            title="Principles, not platitudes"
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {beliefs.map((value, index) => (
              <FadeIn
                key={value.title}
                as="article"
                className="card-premium bg-background p-6 rounded-2xl border border-border"
                delay={index * 100}
              >
                <h3 className="text-lg font-bold mb-3 text-foreground font-display">
                  {value.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {value.description}
                </p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section
        className="relative py-16 md:py-24 bg-background"
        aria-labelledby="our-mission"
      >
        <div
          className="blueprint-grid-gold absolute inset-0"
          aria-hidden="true"
        />
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-8 lg:px-16">
          <div className="max-w-3xl mx-auto text-center">
            <MarketingSectionHeader badge="Our Mission" title="Why we exist" />

            <div className="space-y-4 text-lg text-muted-foreground leading-relaxed">
              <FadeIn as="p">
                Contractor Co-Pilot started after seeing the same problem across
                specialty contractors: skilled tradespeople running their
                businesses on disconnected tools that don&apos;t talk to each
                other.
              </FadeIn>
              <FadeIn as="p" delay={100}>
                The software that existed was either built for enterprise GCs
                with full-time admins, or it was a spreadsheet with a logo.
                Nothing was built specifically for the electrician bidding panel
                upgrades, the landscaper pricing seasonal installs, or the
                concrete contractor quoting flatwork.
              </FadeIn>
              <FadeIn
                as="p"
                className="text-foreground font-semibold"
                delay={200}
              >
                We built the simple, connected system that specialty contractors
                actually need — one that understands your trade and gets out of
                your way.
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <MarketingCTASection
        title="See it for yourself"
        description="7-day free trial. No credit card required. Pick your trade and start estimating."
        primaryCTA={{
          text: "Start Your Free Trial",
          href: "/auth/sign-up",
        }}
        secondaryCTA={{
          text: "Contact Us",
          href: `mailto:${EMAILS.CONTACT}`,
        }}
        trustIndicators={["No long-term contracts | Cancel anytime"]}
      />

      <Footer />
    </div>
  );
}
